import React from 'react';
import { PropertyHealthAudit, HealthRuleId } from '../../../../types/inventoryHealth';
import { HealthIssueBadge } from './HealthIssueBadge';
import { isLotProperty } from '../../../../utils/health/rules/antiguedadRule';
import { Edit3, CheckCircle2, UserCheck } from 'lucide-react';

interface HealthPropertyTableRowProps {
  audit: PropertyHealthAudit;
  onEdit: (audit: PropertyHealthAudit) => void;
  onSelectRuleFilter?: (ruleId: HealthRuleId) => void;
}

export const HealthPropertyTableRow: React.FC<HealthPropertyTableRowProps> = ({
  audit,
  onEdit,
  onSelectRuleFilter,
}) => {
  const p = audit.property;
  const scoreClass =
    audit.score >= 80
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : audit.score >= 50
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-rose-50 text-rose-700 border-rose-200';

  const adminDisplay =
    p.valor_administracion !== undefined && p.valor_administracion !== null
      ? `$${p.valor_administracion.toLocaleString('es-CO')}`
      : 'No registrada';

  const ascensorDisplay =
    p.ascensor !== undefined && p.ascensor !== null
      ? Number(p.ascensor) > 1
        ? `Sí (${p.ascensor})`
        : Number(p.ascensor) === 1 || (p.ascensor as any) === true
        ? 'Sí'
        : 'No'
      : 'Sin asignar';

  const isAntiguedadZero =
    !isLotProperty(p.tipoinmueble) &&
    (p.antiguedad === 0 || (typeof p.antiguedad === 'string' && (p.antiguedad as string).trim() === '0'));

  return (
    <tr id={`health-row-${p.id}`} className="hover:bg-gray-50/80 transition-colors">
      <td className="px-4 py-3 border-b border-gray-100 align-top">
        <div className="flex flex-col">
          <span className="font-black text-xs text-gray-900">{p.codigo || p.id}</span>
          {p.codigo_brp && <span className="text-[10px] text-gray-400 font-bold">BRP: {p.codigo_brp}</span>}
        </div>
      </td>

      <td className="px-4 py-3 border-b border-gray-100 align-top">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-800">{p.tipoinmueble || 'Inmueble'}</span>
          <span className="text-[11px] text-gray-500">{p.ciudad || 'Sin ciudad'}</span>
          {p.owner ? (
            <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold w-fit border border-slate-200">
              <UserCheck className="w-3 h-3 text-slate-500 flex-shrink-0" />
              <span className="truncate max-w-[140px]" title={p.owner}>{p.owner}</span>
            </div>
          ) : (
            <span className="text-[10px] text-gray-400 italic">Sin dueño asignado</span>
          )}
        </div>
      </td>

      <td className="px-4 py-3 border-b border-gray-100 align-top">
        <div className="flex flex-col text-xs font-semibold text-gray-700">
          <span className={p.valor_administracion && p.valor_administracion >= 100000 ? 'text-gray-700' : 'text-rose-600 font-bold'}>
            Adm: {adminDisplay}
          </span>
          <span className={p.piso ? 'text-gray-600' : 'text-rose-600 font-bold'}>
            Piso: {p.piso ?? 'Vacío'}
          </span>
          <span className={p.ascensor !== undefined && p.ascensor !== null ? 'text-gray-600' : 'text-rose-600 font-bold'}>
            Ascensor: {ascensorDisplay}
          </span>
          <span className={isAntiguedadZero ? 'text-amber-600 font-black bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 w-fit mt-0.5' : 'text-gray-500 text-[11px]'}>
            Antigüedad: {p.antiguedad !== null && p.antiguedad !== undefined ? `${p.antiguedad} años` : 'Sin dato'}
          </span>
        </div>
      </td>

      <td className="px-4 py-3 border-b border-gray-100 align-top">
        <span className={`inline-block px-2.5 py-1 rounded-xl text-xs font-black border ${scoreClass}`}>
          {audit.score}%
        </span>
      </td>

      <td className="px-4 py-3 border-b border-gray-100 align-top">
        {audit.isHealthy ? (
          <div className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Sin observaciones</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-w-xl">
            {audit.issues.map((issue, idx) => (
              <HealthIssueBadge
                key={idx}
                issue={issue}
                onRuleClick={() => onSelectRuleFilter?.(issue.ruleId)}
              />
            ))}
          </div>
        )}
      </td>

      <td className="px-4 py-3 border-b border-gray-100 align-top text-right">
        <button
          id={`btn-health-edit-${p.id}`}
          onClick={() => onEdit(audit)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-black uppercase tracking-wider text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-xl transition-colors"
        >
          <Edit3 className="w-3.5 h-3.5" />
          <span>Corregir</span>
        </button>
      </td>
    </tr>
  );
};
